import api from "../api/config";
import { ENDPOINTS } from "../api/endpoints";

/**
 * @typedef {Object} Company
 * @property {number} id - The company's ID
 * @property {string} name - The company's name
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Update timestamp
 */

/**
 * Fetches companies from the database with pagination, search, and sort support
 * @param {Object} options - Query options
 * @param {number} [options.page] - Page number (1-based)
 * @param {number} [options.limit] - Number of items per page
 * @param {string} [options.search] - Search term to filter companies by name
 * @param {string} [options.sortOrder] - Sort order: 'asc' or 'desc' (default: 'asc')
 * @param {Object} messageApi - Optional message API from MessageProvider for toast notifications
 * @returns {Promise<Object>} Object with companies array, total count, page, and limit
 */
export const getAllCompanies = async (options = {}, messageApi = null) => {
  try {
    const { page, limit, search, sortOrder } = options;
    const params = {};
    
    if (page !== undefined && page !== null) {
      params.page = page;
    }
    if (limit !== undefined && limit !== null) {
      params.limit = limit;
    }
    if (search !== undefined && search !== null && search.trim()) {
      params.search = search.trim();
    }
    if (sortOrder !== undefined && sortOrder !== null && (sortOrder === 'asc' || sortOrder === 'desc')) {
      params.sortOrder = sortOrder;
    }

    const response = await api.get(ENDPOINTS.GET_ALL_COMPANIES, { params });

    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    return {
      companies: response.data.data || [],
      total: response.data.total || 0,
      page: response.data.page || 1,
      limit: response.data.limit || (response.data.data || []).length,
    };
  } catch (error) {
    console.error("Error fetching companies:", error);
    if (messageApi) {
      messageApi.error("Failed to fetch companies. Please try again.", 3);
    }
    return {
      companies: [],
      total: 0,
      page: 1,
      limit: 0,
    };
  }
};

/**
 * Creates a new company
 * @param {string} name - Company name
 * @param {Object} messageApi - Optional message API from MessageProvider for toast notifications
 * @returns {Promise<Company>} Created company object with id
 */
export const createCompany = async (name, messageApi = null) => {
  try {
    if (!name || typeof name !== "string" || name.trim() === "") {
      if (messageApi) {
        messageApi.warning("Company name is required", 3);
      }
      throw new Error("Company name is required");
    }

    const response = await api.post(ENDPOINTS.CREATE_COMPANY, {
      name: name.trim(),
    });

    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    return response.data.data;
  } catch (error) {
    console.error("Error creating company:", error);
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      "Failed to create company";
    if (messageApi) {
      messageApi.error(errorMessage, 3);
    }
    throw error;
  }
};